"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  History,
  Eye,
  ArrowLeft,
  Clock,
  TableOfContents,
  TrendingUp,
  Play,
  GitBranch,
  Edit,
  Trash2,
  ChevronRight,
  MessageSquare,
  Square,
  Download,
  ScrollText,
  Terminal,
  Code,
  ChartColumn,
  Cog,
  Loader2,
  Zap,
  Calendar,
  X,
} from "lucide-react";
import { HelpIcon } from "./HelpIcon";
import useSWR from "swr";
import { useSWRConfig } from "swr";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { API_BASE_URL } from "@/lib/config";
import { apiClient } from "@/lib/api-client";
import { FlowchartView } from "@/components/workflow/FlowchartView";
import { RunsOverview } from "@/components/workflow/RunsOverview";
import { RunDetails } from "@/components/workflow/RunDetails";
import { HistoricalRunDetails } from "@/components/workflow/HistoricalRunDetails";

import { ArtifactMessages } from "@/components/artifact-messages";
import { MultimodalInput } from "@/components/multimodal-input";
import { SummaryView } from "@/components/workflow/SummaryView";
import type { VisibilityType } from "@/components/visibility-selector";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { Attachment } from "ai";
import { useArtifact } from "@/hooks/use-artifact";
import { Skeleton } from "@/components/ui/skeleton";
import type { ArtifactCellTarget } from "@/components/artifact";

// Reusable Last Run Display Component
const LastRunDisplay = ({ updatedAt }: { updatedAt: number }) => {
  const parsedDate = new Date(updatedAt * 1000); // Convert Unix seconds to milliseconds
  const formattedDate = format(parsedDate, "EEEE, MMM d, ''yy h:mm a");

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded-md text-[11px]"
      style={{
        background: "rgba(30, 30, 30, 0.6)",
        color: "#fff",
        borderLeft: "2px solid rgba(100, 100, 100, 0.4)",
        borderTop: "1px solid rgba(70, 70, 70, 0.2)",
        borderRight: "1px solid rgba(70, 70, 70, 0.2)",
        borderBottom: "1px solid rgba(70, 70, 70, 0.2)",
      }}
    >
      <span>Last run</span>
      <div
        className="w-px h-3"
        style={{ background: "rgba(120, 120, 120, 0.3)" }}
      ></div>
      <span>{formattedDate}</span>
    </div>
  );
};

// Gradient styles for scroll indicators
const GRADIENT_LEFT =
  "linear-gradient(to right, hsl(var(--background)) 0%, hsl(var(--background)) 20%, hsl(var(--background) / 0.8) 40%, hsl(var(--background) / 0.4) 70%, transparent 100%)";
const GRADIENT_RIGHT =
  "linear-gradient(to left, hsl(var(--background)) 0%, hsl(var(--background)) 30%, hsl(var(--background) / 0.95) 50%, hsl(var(--background) / 0.7) 75%, hsl(var(--background) / 0.3) 90%, transparent 100%)";

type WorkflowView =
  | "plan"
  | "flowchart"
  | "runs"
  | "current-run"
  | "historical-run"
  | "strategy-assistant"
  | "dashboard";

interface WorkflowArtifactProps {
  sessionId: string;
  onRunStrategy?: () => void;
  onAddStep?: (message: string) => void;
  onEditStep?: (stepIndex: number, message: string) => void;
  onEditCell?: (index: number, purpose: string) => void;
  status?: "submitted" | "streaming" | "ready" | "error";
  messages?: Array<any>;
  cellsExecuted?: number;
  // Chat interface props
  input?: string;
  setInput?: UseChatHelpers["setInput"];
  handleSubmit?: UseChatHelpers["handleSubmit"];
  stop?: UseChatHelpers["stop"];
  attachments?: Array<Attachment>;
  setAttachments?: React.Dispatch<React.SetStateAction<Array<Attachment>>>;
  append?: UseChatHelpers["append"];
  setMessages?: UseChatHelpers["setMessages"];
  reload?: UseChatHelpers["reload"];
  votes?: Array<any>;
  isReadonly?: boolean;
  selectedVisibilityType?: VisibilityType;
  onRerunStatusChange?: (isRerunning: boolean, isInitializing: boolean) => void;
  isForkedWorkflow?: boolean;
  isReplay?: boolean;
}
export function WorkflowArtifact({
  sessionId,
  onRunStrategy,
  onAddStep,
  onEditStep,
  onEditCell,
  status = "ready",
  messages = [],
  cellsExecuted = 0,
  // Chat interface props
  input,
  setInput,
  handleSubmit,
  stop,
  attachments,
  setAttachments,
  append,
  setMessages,
  reload,
  votes,
  isReadonly,
  selectedVisibilityType,
  onRerunStatusChange,
  isForkedWorkflow = false,
  isReplay = false,
}: WorkflowArtifactProps) {
  const { user: authUser } = useAuth();
  const { artifact, setArtifact } = useArtifact();

  // Log session ID and user ID
  const [currentView, setCurrentView] = useState<WorkflowView>("current-run");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRunName, setSelectedRunName] = useState<string | null>(null);
  const [processedResultIds, setProcessedResultIds] = useState<Set<string>>(
    new Set()
  );

  // Download state
  const [downloadInput, setDownloadInput] = useState("");
  const [isDownloadPopoverOpen, setIsDownloadPopoverOpen] = useState(false);
  const [downloadingType, setDownloadingType] = useState<
    "summary" | "data" | "code" | null
  >(null);

  // Re-run state (simple!)
  const [isRerunning, setIsRerunning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Schedule run modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleInterest, setScheduleInterest] = useState<number | null>(null);


  const isLocked = isRerunning;

  // Client-side mobile detection for auto-close logic
  const [isMobile, setIsMobile] = useState(false);

  // State for run cells data
  const [runCells, setRunCells] = useState<any[]>([]);
  const [currentCellId, setCurrentCellId] = useState<string | undefined>(
    undefined
  );
  const [currentCellHighlightNonce, setCurrentCellHighlightNonce] =
    useState(0);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [isDeletingCells, setIsDeletingCells] = useState(false);
  const [notebookId, setNotebookId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);
  const [isRegenerateSummaryDialogOpen, setIsRegenerateSummaryDialogOpen] =
    useState(false);

  // Scroll indicator state for button container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // Derive latest dashboard HTML from cell outputs
  const latestDashboardHtml = useMemo(() => {
    for (let i = runCells.length - 1; i >= 0; i--) {
      const cell = runCells[i];
      for (const output of (cell.outputs || [])) {
        if (output.type === "dashboard") {
          return output.data as string;
        }
      }
    }
    return null;
  }, [runCells]);

  // Listen for workflow view changes from tools (e.g., generate_summary)
  useEffect(() => {
    const wv = artifact.workflowView as string | null | undefined;
    if ((wv === "summary" || wv === "dashboard") && (latestDashboardHtml || summary)) {
      setCurrentView("dashboard");
      setArtifact((prev) => ({ ...prev, workflowView: null }));
    }
  }, [artifact.workflowView, summary, setArtifact, latestDashboardHtml]);

  // Listen for expand event from DashboardView cell component
  useEffect(() => {
    const handler = () => setCurrentView("dashboard");
    window.addEventListener("xynth-dashboard-expand", handler);
    return () => window.removeEventListener("xynth-dashboard-expand", handler);
  }, []);

  // Replay: auto-expand to dashboard when dashboard cell first appears
  const prevDashboardHtmlRef = useRef<string | null>(null);
  useEffect(() => {
    if (isReplay && latestDashboardHtml && latestDashboardHtml !== prevDashboardHtmlRef.current) {
      setCurrentView("dashboard");
    }
    prevDashboardHtmlRef.current = latestDashboardHtml;
  }, [isReplay, latestDashboardHtml]);

  // Replay: switch back to workbook when new turn starts streaming (user pressed Continue)
  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (isReplay && prevStatusRef.current !== "streaming" && status === "streaming" && currentView === "dashboard") {
      setCurrentView("current-run");
    }
    prevStatusRef.current = status;
  }, [isReplay, status, currentView]);

  // Session info for filename building
  const { data: sessionInfo } = useSWR(
    sessionId && !isReplay ? ["session-info", sessionId, authUser?.uid] : null,
    () => apiClient.getSession(sessionId, authUser?.uid || undefined)
  );

  // Update gradient visibility based on scroll position
  const updateGradientVisibility = (element: HTMLDivElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = element;
    setShowLeftGradient(scrollLeft > 10);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
  };

  // Handle scroll to update gradient indicators
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateGradientVisibility(e.currentTarget);
  };

  // Ref callback to check scroll state when container mounts
  const handleScrollContainerRef = (element: HTMLDivElement | null) => {
    if (element) {
      updateGradientVisibility(element);
    }
  };

  const focusCellById = (cellId: string) => {
    setCurrentCellId(cellId);
    setCurrentCellHighlightNonce((prev) => prev + 1);
    const cellElement = document.getElementById(`cell-${cellId}`);
    if (cellElement) {
      cellElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const resolveCellTarget = (target: ArtifactCellTarget) => {
    if (target.cellId) {
      const idMatch = runCells.find(
        (cell) => String(cell?.id) === String(target.cellId)
      );
      if (idMatch) {
        return idMatch;
      }
    }

    if (typeof target.cellIndex === "number") {
      const indexMatch = runCells.find((cell) => cell?.index === target.cellIndex);
      if (indexMatch) {
        return indexMatch;
      }
    }

    return null;
  };

  // Handler for cell click from sidebar - scrolls to cell
  const handleSidebarCellClick = (cellId: string) => {
    setCurrentView("current-run");
    focusCellById(cellId);
  };

  useEffect(() => {
    const target = artifact.pendingCellTarget;

    if (!target || !artifact.isVisible) {
      return;
    }

    if (currentView !== "current-run") {
      setCurrentView("current-run");
      return;
    }

    if (!Array.isArray(runCells) || runCells.length === 0) {
      return;
    }

    const matchedCell = resolveCellTarget(target);
    if (!matchedCell?.id) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      focusCellById(String(matchedCell.id));
      setArtifact((prev) => {
        if (prev.pendingCellTarget?.requestedAt !== target.requestedAt) {
          return prev;
        }

        return {
          ...prev,
          pendingCellTarget: null,
        };
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [
    artifact.isVisible,
    artifact.pendingCellTarget,
    currentView,
    runCells,
    setArtifact,
  ]);

  const handleDashboardClick = async () => {
    setCurrentView("dashboard");
    // If we have dashboard HTML from cells, just show it
    if (latestDashboardHtml) return;
    // Fall back to markdown summary
    if (summary) return;
    if (!notebookId) {
      toast.error("Notebook ID not available yet");
      return;
    }
    if (isSummaryGenerating || isLocked || isReadonly) return;
    setIsSummaryGenerating(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await apiClient.get(
        `/notebook/summary/${notebookId}?timezone=${encodeURIComponent(
          timezone
        )}`
      );
      await mutate(
        `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
      );
      const resp = await fetch(
        `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
      );
      const data = await resp.json();
      setSummary(data?.summary || null);
    } catch (error) {
      toast.error("Failed to generate summary");
    } finally {
      setIsSummaryGenerating(false);
    }
  };

  const handleRegenerateSummary = async () => {
    if (!notebookId) {
      toast.error("Notebook ID not available yet");
      return;
    }
    if (isSummaryGenerating || isLocked || isReadonly) return;

    setIsSummaryGenerating(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await apiClient.get(
        `/notebook/summary/${notebookId}?timezone=${encodeURIComponent(
          timezone
        )}`
      );
      await mutate(
        `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
      );
      const resp = await fetch(
        `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
      );
      const data = await resp.json();
      setSummary(data?.summary || null);
    } catch (error) {
      toast.error("Failed to regenerate summary");
    } finally {
      setIsSummaryGenerating(false);
    }
  };

  // Popover states
  const [isRunPopoverOpen, setIsRunPopoverOpen] = useState(false);
  const [isEditPopoverOpen, setIsEditPopoverOpen] = useState(false);
  const [runDescription, setRunDescription] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const { mutate } = useSWRConfig();
  // Handle re-run completion (called by RunDetails when status becomes "success" or "error")
  const handleRerunComplete = (status: "success" | "error") => {
    setIsRerunning(false);
    if (status === "success") {
      toast.success("Re-run completed successfully!");
      setCurrentView("dashboard");
    } else {
      toast.error("Re-run failed with errors");
    }
  };

  // Handle resuming polling when execution detected (e.g., after refresh)
  const handleResumePolling = () => {
    setIsRerunning(true);
  };

  const handleViewRuns = () => {
    setCurrentView("runs");
  };

  const handleViewPlan = () => {
    setCurrentView("plan");
    setSelectedRunId(null);
  };

  const handleViewRunDetails = (runId: string, runData: any) => {
    if (runData?.isLatest) {
      // Navigate to current run view for the latest run
      setCurrentView("current-run");
      setSelectedRunId(null);
      setSelectedRunName(null);
    } else {
      // Show historical run view for older runs
      setSelectedRunId(runId);
      setCurrentView("historical-run");
      // Use the run name directly from runData - no API call needed!
      if (runData?.name) {
        const truncatedName =
          runData.name.length > 20
            ? runData.name.substring(0, 20) + "..."
            : runData.name;
        setSelectedRunName(truncatedName);
      } else {
        setSelectedRunName(null);
      }
    }
  };

  const handleBackToRuns = () => {
    setCurrentView("runs");
    setSelectedRunName(null);
    setSelectedRunId(null);
  };

  const handleRunsClick = () => {
    setCurrentView("runs");
  };

  const handleLastRunClick = () => {
    setCurrentView("current-run");
    setSelectedRunId(null);
  };

  const handleStrategyAssistantClick = () => {
    setCurrentView("strategy-assistant");
  };

  // Popover handlers
  const handleRunStrategy = () => {
    if (runDescription.trim() && onAddStep) {
      onAddStep(`Run strategy: ${runDescription.trim()}`);
    }
    setIsRunPopoverOpen(false);
    setRunDescription("");
  };

  const handleEditStrategy = () => {
    if (editDescription.trim() && onAddStep) {
      onAddStep(`Edit strategy: ${editDescription.trim()}`);
    }
    setIsEditPopoverOpen(false);
    setEditDescription("");
  };

  const handleRunPopoverClose = () => {
    setIsRunPopoverOpen(false);
    setRunDescription("");
  };

  const handleEditPopoverClose = () => {
    setIsEditPopoverOpen(false);
    setEditDescription("");
  };

  const handleClearStrategy = async () => {
    try {
      // Call DELETE API endpoint to clear all steps
      const response = await fetch(
        `${API_BASE_URL}/strategies/strategy-plan-steps/${sessionId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        // Refresh the cache to reload the data
        mutate(
          `${API_BASE_URL}/strategies/strategy-plan-by-sessionID/${sessionId}`
        );
      } else {
      }
    } catch (error) {
    }
  };

  const handleDeleteAllCells = async () => {
    if (!authUser?.uid || !runCells.length || isDeletingCells) return;

    setIsDeletingCells(true);
    try {
      // Get all cell indices
      const cellIndices = runCells.map((cell) => cell.index);
      toast.info("Notebook clearing initiated");

      // Call POST /agent/execute-tool
      const response = await fetch(`${API_BASE_URL}/agent/execute-tool`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: authUser.uid,
          session_id: sessionId,
          tool_name: "delete_cells",
          tool_arguments: {
            cell_indices: cellIndices,
          },
        }),
      });
      if (!response.ok) {
        toast.error("Failed to clear notebook");
      } else {
        // Invalidate SWR cache to refetch updated data
        mutate(`${API_BASE_URL}/strategies/active-run-details/${sessionId}`);
        toast.success("Notebook cleared successfully");
      }
    } catch (error) {
      toast.error("Failed to clear notebook");
    } finally {
      setIsDeletingCells(false);
    }
  };

  const handleDownloadSend = () => {
    if (!downloadInput.trim() || !append) return;

    const textToSend = downloadInput;

    // Close popover and clear input
    setIsDownloadPopoverOpen(false);
    setTimeout(() => {
      setDownloadInput("");
    }, 300);

    // Send message
    append({
      role: "user",
      content: textToSend,
    });
  };

  const handleDownloadCancel = () => {
    setIsDownloadPopoverOpen(false);
    setTimeout(() => {
      setDownloadInput("");
    }, 300);
  };

  // Trigger a browser download from a Blob
  const saveBlob = (
    blob: Blob,
    filename?: string,
    fallback: string = "download"
  ) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || fallback;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const makeSafeFilename = (raw: string) =>
    (raw || "")
      .replace(/[^a-z0-9-_\.]+/gi, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 120);

  const buildFilename = (
    type: "summary" | "data" | "code",
    contentType?: string | undefined
  ) => {
    const baseTitle = makeSafeFilename(
      sessionInfo?.title || sessionId || "notebook"
    );
    const choice =
      type === "summary" ? "summary" : type === "data" ? "data" : "code";
    let ext = "txt";
    if (type === "code") {
      ext = "py"; // force .py for code
    } else if (type === "summary") {
      ext = "pdf";
    } else if (type === "data") {
      if (contentType?.includes("zip")) ext = "zip";
      else if (contentType?.includes("json")) ext = "json";
      else ext = "zip"; // sensible default for data bundles
    }
    return `${baseTitle}-${choice}.${ext}`;
  };

  // Handle direct download actions
  const handleDirectDownload = async (type: "summary" | "data" | "code") => {
    if (!notebookId) {
      toast.error("Notebook ID not available yet");
      return;
    }
    setDownloadingType(type);
    try {
      const { blob, filename, contentType } = await apiClient.downloadNotebook(
        notebookId,
        type
      );
      const desiredName = buildFilename(type, contentType);
      // Prefer our naming scheme; fallback uses desiredName too
      saveBlob(blob, desiredName, desiredName);
      toast.success(
        `${type.charAt(0).toUpperCase() + type.slice(1)} downloaded`
      );
      setIsDownloadPopoverOpen(false);
    } catch (err: any) {
      toast.error(err?.message || `Failed to download ${type}`);
    } finally {
      setDownloadingType(null);
    }
  };

  const handleStopExecution = async () => {
    if (!notebookId) {
      toast.error("Notebook ID not available");
      return;
    }

    try {
      await apiClient.stopNotebookExecution(notebookId);
      setIsRerunning(false);
      setIsInitializing(false);
      toast.success("Execution stopped");
      mutate(`${API_BASE_URL}/strategies/active-run-details/${sessionId}`);
    } catch (error) {
      toast.error("Failed to stop execution");
      setIsRerunning(false);
      setIsInitializing(false);
    }
  };

  // Notify parent when rerun status changes
  useEffect(() => {
    if (onRerunStatusChange) {
      // Propagate locked execution so chat locks while workflow is executing
      onRerunStatusChange(isLocked, isInitializing);
    }
  }, [isLocked, isInitializing, onRerunStatusChange]);

  const renderContent = () => {
    switch (currentView) {
      case "runs":
        return (
          <RunsOverview
            sessionId={sessionId}
            onViewRunDetails={handleViewRunDetails}
            onBackToPlan={() => setCurrentView("plan")}
          />
        );

      case "current-run":
        return (
          <RunDetails
            sessionId={sessionId}
            onBackToRuns={handleBackToRuns}
            onBackToPlan={handleViewPlan}
            cellsExecuted={cellsExecuted}
            onCellsUpdate={setRunCells}
            onCurrentCellChange={setCurrentCellId}
            isRerunning={isLocked}
            onRerunComplete={handleRerunComplete}
            onResumePolling={handleResumePolling}
            onEditCell={onEditCell}
            onNotebookIdChange={setNotebookId}
            onSummaryChange={setSummary}
            onUpdatedAtChange={setUpdatedAt}
            isReadonly={sessionInfo?.read_only || isReadonly}
            isForkedWorkflow={isForkedWorkflow}
            chatStatus={status}
            isReplay={isReplay}
            focusedCellId={currentCellId}
            focusedCellAnimationKey={currentCellHighlightNonce}
          />
        );

      case "historical-run":
        return selectedRunId ? (
          <HistoricalRunDetails
            runId={selectedRunId}
            onBackToRuns={handleBackToRuns}
            onBackToPlan={handleViewPlan}
            onCellsUpdate={setRunCells}
            onCurrentCellChange={setCurrentCellId}
            focusedCellId={currentCellId}
            focusedCellAnimationKey={currentCellHighlightNonce}
          />
        ) : null;

      case "dashboard": {
        // Prefer dashboard HTML from cells, fall back to markdown summary
        if (latestDashboardHtml) {
          return (
            <div className="flex flex-col h-full overflow-hidden relative">
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border shrink-0">
                <button
                  onClick={() => setCurrentView("current-run")}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Back to Workbook</span>
                </button>
                <div className="w-px h-3 bg-border" />
                <button
                  onClick={() => {
                    setCurrentView("current-run");
                    setArtifact((prev) => ({ ...prev, isVisible: false }));
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>Back to Chat</span>
                </button>
              </div>
              <iframe
                srcDoc={latestDashboardHtml}
                style={{
                  flex: 1,
                  width: "100%",
                  border: "none",
                  backgroundColor: "transparent",
                }}
                title="Research Dashboard"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          );
        }
        if (isSummaryGenerating) {
          return (
            <div className="flex-1 overflow-y-auto p-6 relative">
              <div className="max-w-4xl mx-auto space-y-3">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          );
        }
        return (
          <div className="relative h-full">
            <SummaryView summary={summary || ""} />
          </div>
        );
      }

      default:
        return null;
    }
  };

  const handleCloseArtifact = () => {
    setArtifact((prev) => ({
      ...prev,
      isVisible: false,
    }));
  };

  return (
    <div
      className={`flex flex-col h-full ${
        isLocked || isInitializing ? "workflow-running-glow" : ""
      } w-full min-h-0 transition-all duration-300 relative`}
    >
      {/* Header with action buttons */}
      <div className="sticky top-0 z-10 flex items-center gap-2">
        {/* Scrollable left-side buttons with gradient indicators */}
        <div className="relative max-w-[calc(100vw-100px)] md:max-w-none">
          {/* Left gradient indicator */}
          <div
            className="absolute left-0 top-0 bottom-0 w-16 pointer-events-none z-10 md:hidden transition-opacity duration-300"
            style={{
              background: GRADIENT_LEFT,
              opacity: showLeftGradient ? 1 : 0,
            }}
          />

          {/* Right gradient indicator */}
          <div
            className="absolute top-0 bottom-0 w-28 pointer-events-none z-10 md:hidden transition-opacity duration-300"
            style={{
              right: "-16px",
              background: GRADIENT_RIGHT,
              opacity: showRightGradient ? 1 : 0,
            }}
          />

          <div
            ref={handleScrollContainerRef}
            onScroll={handleScroll}
            className="flex items-center gap-2 overflow-x-auto md:overflow-x-visible scrollbar-hide scroll-smooth"
          >
            {isReplay ? (
            <span className="text-xs text-zinc-500 ml-2">Replay</span>
          ) : isRerunning ? (
              <div className="flex items-center gap-1.5 ml-2">
                <button
                  onClick={handleStopExecution}
                  className="h-4 w-4 flex items-center justify-center rounded-full stop-button-glow"
                >
                  <Square className="h-2 w-2 text-red-500 fill-red-500" />
                </button>
                <span className="text-xs text-muted-foreground">Running</span>
              </div>
            ) : null}

            <Button
              variant="ghost"
              onClick={() => toast("Coming soon")}
              className="h-7 px-3 bg-transparent text-foreground hover:bg-accent flex items-center gap-1.5 shrink-0"
            >
              <ChartColumn className="h-3.5 w-3.5" />
              <span className="text-xs">Dashboard</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => toast("Coming soon")}
              className="h-7 px-3 bg-transparent text-foreground hover:bg-accent flex items-center gap-1.5 shrink-0"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="text-xs">Download</span>
            </Button>
          </div>
        </div>

        {/* Right-side spacer */}
        <div className="ml-auto shrink-0" />

        {/* Back to Notebook */}
        <div className="ml-6">
          <Button
            variant="ghost"
            onClick={handleCloseArtifact}
            className="h-7 gap-1.5 rounded-md border border-border/50 bg-background/40 px-3 text-xs font-medium text-foreground/85 backdrop-blur-sm hover:bg-muted/50 hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="text-xs">Back to Notebook</span>
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden h-0">{renderContent()}</div>

    </div>
  );
}
